import type { TParentRunLabelKey } from "../types/agent-invocation";
import { EParentAutomationKind } from "../types/agent-invocation";

export const CRON_JOBS_QUERY_KEY = "cronJobs";

// NOTE: NSAI-6655 — the bounds of an agent run's timeout, in seconds, that the
// server DTOs enforce (MIN_TIMEOUT_SECONDS / MAX_TIMEOUT_SECONDS in
// apps/nsai/server/src/automation/constants/scheduler.constants.ts). Keep the
// two in step: a form that allows less than the server only turns a field
// error into a failed save.
export const MIN_AGENT_TIMEOUT_SECONDS = 60;
export const MAX_AGENT_TIMEOUT_SECONDS = 3_600;

// NOTE: NSAI-5610 — the "Run as assistant" form value meaning "no assistant,
// plain model". Forms hold a string; the payload helpers in
// utils/automation-assistant.ts turn it into an omitted key (create) or `null`
// (update, when a binding is being cleared).
export const NO_AUTOMATION_ASSISTANT_VALUE = "";

// NOTE: NSAI-5636 — the longest output criterion an agent may store, the bound
// the server DTOs enforce (MAX_OUTPUT_CRITERION_LENGTH in
// apps/nsai/server/src/output-guardrails/output-guardrail.constants.ts).
export const MAX_OUTPUT_CRITERION_LENGTH = 2_000;
export const CRON_JOB_RUNS_QUERY_KEY = "cronJobRuns";
export const WEBHOOKS_QUERY_KEY = "webhooks";
export const WEBHOOK_RUNS_QUERY_KEY = "webhookRuns";
export const HOOKS_QUERY_KEY = "hooks";
export const HOOK_RUNS_QUERY_KEY = "hookRuns";

// NOTE: NSAI-5417 — the request header the public webhook ingress
// (POST /api/hooks/:webhookUuid) reads the trigger secret from. Clients hand the
// secret out for this header and never bake it into the copied URL, so it stays
// out of proxy / access logs, shell history and chat pastes.
export const WEBHOOK_TOKEN_HEADER = "x-webhook-token";

// NOTE: NSAI-5417 — the shell variable the copied cURL example reads the token
// from, so running the command never writes the secret into shell history.
export const WEBHOOK_TOKEN_ENV_VAR = "NSAI_WEBHOOK_TOKEN";

// NOTE: NSAI-5272 — sharing + marketplace query keys.
export const AUTOMATION_SHARES_QUERY_KEY = "automationShares";
export const AUTOMATION_MARKETPLACE_QUERY_KEY = "automationMarketplace";
// NOTE: NSAI-5266 — react-query keys for the per-run detail (trace) fetch, one per
// family. The web/mobile hooks append [uuid, runUuid].
export const CRON_JOB_RUN_DETAIL_QUERY_KEY = "cronJobRunDetail";
export const WEBHOOK_RUN_DETAIL_QUERY_KEY = "webhookRunDetail";
export const HOOK_RUN_DETAIL_QUERY_KEY = "hookRunDetail";
// NOTE: NSAI-7883 — a run's child runs; the web/mobile hooks append [runUuid].
export const AUTOMATION_RUN_CHILDREN_QUERY_KEY = "automationRunChildren";
// NOTE: NSAI-7883 — mirrors the server's cap on GET
// /automation/runs/:runUuid/children (MAX_CHILD_RUNS in
// automation-run-tree.constants.ts); the clients say so when a list hits it.
export const MAX_LISTED_CHILD_RUNS = 50;

// NOTE: NSAI-7883 — the "Started by …" key per parent kind: literal keys, so
// they stay greppable and type-checked.
export const PARENT_RUN_LABEL_KEYS: Readonly<
  Record<EParentAutomationKind, TParentRunLabelKey>
> = {
  [EParentAutomationKind.AGENT]: "runs.startedByAgent",
  [EParentAutomationKind.WORKFLOW]: "runs.startedByWorkflow",
  [EParentAutomationKind.WEBHOOK]: "runs.startedByWebhook",
  [EParentAutomationKind.EVENT_RULE]: "runs.startedByEventRule",
};

// NOTE: NSAI-5266 — run status values, mirroring the server's EAutomationRunStatus
// (server/src/automation/automation.types.ts). `RUNNING` is the live badge added by
// create-at-start; older rows are only OK/ERROR/SKIPPED.
export enum EAgentRunStatus {
  OK = "ok",
  ERROR = "error",
  SKIPPED = "skipped",
  RUNNING = "running",
}

// NOTE: NSAI-5266 — how a run was triggered, mirroring the server's
// EAutomationRunTriggerType. Null on pre-feature rows.
export enum EAutomationRunTriggerType {
  MANUAL = "manual",
  SCHEDULED = "scheduled",
  WEBHOOK = "webhook",
  EVENT = "event",
}

// NOTE: NSAI-5266 — tool names that are internal orchestration (tool discovery /
// activation), never user-meaningful actions, so they are omitted from a run's
// step trace. New runs already skip recording these server-side; this also hides
// them on runs recorded before that skip landed.
export const HIDDEN_RUN_STEP_TOOL_NAMES: readonly string[] = [
  "searchTools",
] as const;

// NOTE: NSAI-5266 — per-step status in a run trace, mirroring the server's
// EAgentRunStepStatus.
export enum EAgentRunStepStatus {
  RUNNING = "running",
  OK = "ok",
  ERROR = "error",
  SKIPPED = "skipped",
}

// NOTE: NSAI-5266 — kind of a run-trace step, mirroring the server's
// EAgentRunStepType. NSAI-6948 — TOOL_CALL for each tool call and TEXT for each
// model turn are recorded; REASONING is never written.
export enum EAgentRunStepType {
  TOOL_CALL = "tool_call",
  TEXT = "text",
  REASONING = "reasoning",
}

// NOTE: NSAI-5266 — which automation family a run belongs to. Shared by web + mobile
// to route a run's step-trace fetch to the right detail endpoint (values match the
// server's EAutomationRunType).
export enum EAutomationRunFamily {
  CRON = "cron",
  WEBHOOK = "webhook",
  HOOK = "hook",
}

// NOTE: Must match ANOMALY_THRESHOLDS.AUTOMATION_RUNS.maxCount on the server (server/src/audit/types/audit.types.ts).
export const AUTOMATION_RUNS_MAX_PER_HOUR = 60 as const;

// NOTE: Event-rule (hook) trigger options for the create/edit dialogs on web
// AND mobile — single source of truth so the two dropdowns can't drift.
// `value` mirrors the server's EAutomationEventType raw values
// (server/src/automation/automation.types.ts); `labelKey` resolves in the
// `agents` i18n namespace (i18n/locales/*/agents.json → hooks.eventTypes).
export const HOOK_EVENT_TYPE_OPTIONS = [
  {
    value: "cron.run.completed",
    labelKey: "hooks.eventTypes.cronRunCompleted",
  },
  { value: "cron.run.failed", labelKey: "hooks.eventTypes.cronRunFailed" },
  {
    value: "webhook.run.completed",
    labelKey: "hooks.eventTypes.webhookRunCompleted",
  },
  {
    value: "webhook.run.failed",
    labelKey: "hooks.eventTypes.webhookRunFailed",
  },
  {
    value: "hook.run.completed",
    labelKey: "hooks.eventTypes.hookRunCompleted",
  },
  { value: "hook.run.failed", labelKey: "hooks.eventTypes.hookRunFailed" },
  { value: "chat.created", labelKey: "hooks.eventTypes.chatCreated" },
  { value: "chat.message.sent", labelKey: "hooks.eventTypes.chatMessageSent" },
  {
    value: "integration.connected",
    labelKey: "hooks.eventTypes.integrationConnected",
  },
  {
    value: "integration.disconnected",
    labelKey: "hooks.eventTypes.integrationDisconnected",
  },
  {
    value: "project.member.added",
    labelKey: "hooks.eventTypes.projectMemberAdded",
  },
  {
    value: "project.member.removed",
    labelKey: "hooks.eventTypes.projectMemberRemoved",
  },
  {
    value: "meeting.processed",
    labelKey: "hooks.eventTypes.meetingProcessed",
  },
  { value: "meeting.failed", labelKey: "hooks.eventTypes.meetingFailed" },
] as const;

// NOTE: Event types the server can emit (EAutomationEventType in
// server/src/automation/automation.types.ts) that the create/edit dropdown does NOT
// offer. `CreateHookDto.eventType` is a plain string, so a rule can be stored against
// one of these via the API even though no UI lets you pick it. They are kept out of
// HOOK_EVENT_TYPE_OPTIONS on purpose — adding them there would change what users can
// create — but they still need a readable name wherever a saved rule is listed
// (NSAI-4696), so getHookEventTypeLabelKey resolves both lists.
export const NON_SELECTABLE_HOOK_EVENT_TYPE_OPTIONS = [
  {
    value: "workflow.run.completed",
    labelKey: "hooks.eventTypes.workflowRunCompleted",
  },
  {
    value: "workflow.run.failed",
    labelKey: "hooks.eventTypes.workflowRunFailed",
  },
] as const;
